// Link helpers for editor-authored free text. `links_notes` and narratives
// are plain textareas, so URLs arrive bare; these pull them out and give
// each a short human label ("Google Doc", "Linear ENG-123") so cards can
// show them as real links instead of a wall of query strings.

/** Bare http(s) URL. Stops at whitespace and the closing bracket/angle of
 *  surrounding markup; trailing sentence punctuation is trimmed after. */
export const BARE_URL_SOURCE = String.raw`https?:\/\/[^\s<>()\[\]]+`;

/** Drop trailing punctuation that belongs to the sentence, not the URL
 *  ("see https://x.com/doc." → "https://x.com/doc"). */
export function trimUrl(raw: string): string {
  return raw.replace(/[.,;:!?'"]+$/, "");
}

export function linkLabel(href: string): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href;
  }
  const host = url.hostname.replace(/^www\./, "");
  const path = url.pathname;
  if (host === "docs.google.com") {
    if (path.startsWith("/document")) return "Google Doc";
    if (path.startsWith("/presentation")) return "Google Slides";
    if (path.startsWith("/spreadsheets")) return "Google Sheet";
    if (path.startsWith("/forms")) return "Google Form";
    return "Google Docs";
  }
  if (host === "drive.google.com") return "Google Drive";
  if (host === "linear.app") {
    const issue = path.match(/\/issue\/([A-Z]+-\d+)/i);
    return issue ? `Linear ${issue[1]!.toUpperCase()}` : "Linear";
  }
  if (host === "github.com") {
    const pr = path.match(/\/pull\/(\d+)/);
    if (pr) return `GitHub PR #${pr[1]}`;
    const issue = path.match(/\/issues\/(\d+)/);
    if (issue) return `GitHub #${issue[1]}`;
    return "GitHub";
  }
  if (host.endsWith("figma.com")) return "Figma";
  if (host.endsWith("notion.so") || host.endsWith("notion.site")) return "Notion";
  if (host.endsWith("loom.com")) return "Loom";
  if (host.endsWith("slack.com")) return "Slack";
  if (host.endsWith("youtube.com") || host === "youtu.be") return "YouTube";
  return host;
}

export interface ExtractedLink {
  href: string;
  label: string;
}

const LINK_OR_URL_RE = new RegExp(
  String.raw`\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)|(${BARE_URL_SOURCE})`,
  "g",
);

/** Every distinct URL in `text`, in order, labeled. Picks up both bare URLs
 *  and markdown `[text](url)` links (using the author's text as the label). */
export function extractLinks(text: string | null | undefined): ExtractedLink[] {
  if (!text) return [];
  const out: ExtractedLink[] = [];
  const seen = new Set<string>();
  for (const m of text.matchAll(LINK_OR_URL_RE)) {
    const href = m[2] ?? trimUrl(m[3]!);
    if (seen.has(href)) continue;
    seen.add(href);
    out.push({ href, label: m[1] ?? linkLabel(href) });
  }
  return out;
}
